/**
 * LiveSiteEventView.tsx
 * ----------------
 * Public portal section for viewing details of a live site event.
 */

import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { getLiveSiteEvent, setLiveSiteEventViewContext } from '../../actions/shared';
import { useImmutableSelector } from '../../hooks/useImmutableSelector';
import {
    selectLiveSiteEvent, selectLiveSiteEventError, selectLiveSiteEventStatus, selectLiveSiteEventViewId,
} from '../../selectors/shared/liveSiteEvent';
import { ErrorCard } from '../shared/ErrorCard';
import { LiveSiteEventDetailsCard } from '../shared/LiveSiteEventDetailsCard';
import { LiveSiteEventLogsCard } from '../shared/LiveSiteEventLogsCard';

/**
 * Component properties
 */

interface LiveSiteEventViewProps {
    basePageTitle: string;
    id: number;
    showIcmId?: boolean;
    useLatestData?: boolean;
}

/**
 * Private interfaces & types
 */

interface Context {
    id: number | undefined;
}

/**
 * Private functions
 */

function getContext (id: number | undefined): Context {
    return { id };
}

function hasContextChanged (previousContext: Context, currentContext: Context) {
    const { id: previousId } = previousContext;
    const { id } = currentContext;

    return id !== previousId;
}

/**
 * Public portal section for viewing details of a live site event.
 * @param props Component properties
 * @returns Rendered content
 */
export function LiveSiteEventView (props: LiveSiteEventViewProps) {
    const { basePageTitle, id, showIcmId, useLatestData } = props;
    const requiresLatestData = useLatestData !== undefined ? useLatestData : false;

    // Action hooks
    const dispatch = useDispatch();

    // Application state hooks
    const liveSiteEvent = useImmutableSelector(selectLiveSiteEvent);
    const liveSiteEventError = useImmutableSelector(selectLiveSiteEventError);
    const liveSiteEventStatus = useImmutableSelector(selectLiveSiteEventStatus);
    const liveSiteEventViewId = useImmutableSelector(selectLiveSiteEventViewId);

    // Effect hooks
    // Effect: set page title to match the loaded live site event
    React.useEffect(() => {
        document.title = liveSiteEvent !== undefined
            ? `${basePageTitle}: live site event ${id} - "${liveSiteEvent.title}"`
            : `${basePageTitle}: live site event ${id}`;
    }, [basePageTitle, id, liveSiteEvent]);

    // Update context when it's changed
    const previousContext = getContext(liveSiteEventViewId);
    const currentContext = getContext(id);

    if (hasContextChanged(previousContext, currentContext)) {
        dispatch(setLiveSiteEventViewContext(id));
    }

    // Reload live site event if status for live site event is none
    if (liveSiteEventStatus === 'none') {
        dispatch(getLiveSiteEvent(id, requiresLatestData));
    }

    // Failed state: show error card
    if (liveSiteEventStatus === 'failed') {
        return (
            <div className="live-site-event-view">
                <div className="live-site-event-view__error-card-container">
                    <ErrorCard
                        message={liveSiteEventError}
                        title="Failed to load live site event" />
                </div>
            </div>
        );
    }

    // Loading state: show a spinner
    if (liveSiteEventStatus === 'inProgress') {
        return (
            <div className="live-site-event-view">
                <div className="live-site-event-view__loading-container">
                    <Spinner
                        className="live-site-event-view__spinner"
                        label="Loading live site event data..."
                        size={SpinnerSize.large} />
                </div>
            </div>
        );
    }

    // Ambiguous state: empty page
    if (liveSiteEvent === undefined) {
        return (
            <div className="live-site-event-view">
                <div className="live-site-event-view__empty-container">

                </div>
            </div>
        );
    }

    const { icmId, impactStartTimestamp, impacts, lastUpdatedTimestamp, logs, severity, title } = liveSiteEvent;

    const icmIdProps = showIcmId === true ? { icmId } : {};

    // TODO: make sure the timestamp sorting works as expected
    const timestampProps = impacts.every(i => i.state === 'Resolved')
        ? { resolvedTimestamp: impacts.map(i => i.resolvedTimestamp as string).sort().reverse()[0] }
        : { lastUpdatedTimestamp };

    return (
        <div className="live-site-event-view">
            <div className="live-site-event-view__details-container">
                <LiveSiteEventDetailsCard
                    impactStartTimestamp={impactStartTimestamp}
                    impacts={impacts}
                    service={impacts[0].service}
                    severity={severity}
                    title={title}
                    {...icmIdProps}
                    {...timestampProps} />
            </div>

            <div className="live-site-event-view__logs-container">
                <LiveSiteEventLogsCard
                    logs={logs.map(log => ({
                        author: log.lastUpdatedBy,
                        text: log.text,
                        timestamp: log.createdTimestamp,
                        type: log.type,
                    }))} />
            </div>
        </div>
    );
}
